import React, { useState } from 'react';
import { useAuth } from '@/ui/auth/AuthProvider';
import { saveApiKeys, type ApiKeyProvider } from '@/lib/api-keys';
import { Key, CheckCircle2, ShieldCheck, Loader2 } from 'lucide-react';

interface FirstRunWizardProps {
    onComplete: () => void;
}

export function FirstRunWizard({ onComplete }: FirstRunWizardProps) {
    const { user } = useAuth();
    const [keys, setKeys] = useState<Partial<Record<ApiKeyProvider, string>>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleKeyChange = (provider: ApiKeyProvider, value: string) => {
        setKeys(prev => ({ ...prev, [provider]: value }));
    };

    const handleSave = async (e?: React.FormEvent) => {
        e?.preventDefault();

        // Trim all keys
        const trimmedKeys: Partial<Record<ApiKeyProvider, string>> = {};
        Object.entries(keys).forEach(([provider, key]) => {
            if (key && typeof key === 'string') {
                trimmedKeys[provider as ApiKeyProvider] = key.trim();
            }
        });

        // Validate at least one key is present
        const hasKey = Object.values(trimmedKeys).some(k => k && k.length > 0);
        if (!hasKey) {
            setError('Please add at least one API key to get started.');
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            // Add a timeout to prevent infinite spinning
            const savePromise = saveApiKeys(trimmedKeys);
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Saving timed out. Please check your connection.')), 10000)
            );

            await Promise.race([savePromise, timeoutPromise]);
            onComplete();
        } catch (err) {
            console.error('Failed to save keys:', err);
            setError((err as Error).message || 'Failed to save keys. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="bg-background-secondary border border-border rounded-xl w-full max-w-2xl overflow-hidden flex flex-col shadow-2xl">

                {/* Header */}
                <div className="p-8 border-b border-border bg-background-tertiary/50">
                    <div className="flex items-center gap-4 mb-2">
                        <div className="w-12 h-12 rounded-full bg-accent/20 text-accent flex items-center justify-center">
                            <Key className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Setup Your AI Keys</h1>
                            <p className="text-foreground-muted">
                                Connect your preferred AI providers to start using HiveMind.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <form onSubmit={handleSave} className="flex flex-col flex-1">
                    <div className="p-8 space-y-6 flex-1 overflow-y-auto">
                        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 flex items-start gap-3">
                            <ShieldCheck className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-200">
                                <p className="font-semibold mb-1">Your keys are secure</p>
                                <p>
                                    Keys are encrypted with your unique ID before being stored.
                                    They are never shared with anyone and are only used to communicate directly with AI providers.
                                </p>
                            </div>
                        </div>

                        <div className="grid gap-4">
                            {/* OpenRouter */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium flex items-center justify-between">
                                    <span>OpenRouter (Recommended)</span>
                                    <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">
                                        Get Key →
                                    </a>
                                </label>
                                <input
                                    type="password"
                                    placeholder="sk-or-..."
                                    value={keys.openrouter || ''}
                                    onChange={(e) => handleKeyChange('openrouter', e.target.value)}
                                    className="w-full bg-background border border-border rounded-lg px-4 py-2 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all"
                                    autoComplete="new-password"
                                />
                                <p className="text-xs text-foreground-muted">
                                    Access all models (OpenAI, Anthropic, Google, etc.) with a single key.
                                </p>
                            </div>

                            {/* Google */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium flex items-center justify-between">
                                    <span>Google Gemini</span>
                                    <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">
                                        Get Key →
                                    </a>
                                </label>
                                <input
                                    type="password"
                                    placeholder="AIza..."
                                    value={keys.google || ''}
                                    onChange={(e) => handleKeyChange('google', e.target.value)}
                                    className="w-full bg-background border border-border rounded-lg px-4 py-2 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all"
                                    autoComplete="new-password"
                                />
                            </div>

                            {/* OpenAI */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium flex items-center justify-between">
                                    <span>OpenAI</span>
                                    <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">
                                        Get Key →
                                    </a>
                                </label>
                                <input
                                    type="password"
                                    placeholder="sk-..."
                                    value={keys.openai || ''}
                                    onChange={(e) => handleKeyChange('openai', e.target.value)}
                                    className="w-full bg-background border border-border rounded-lg px-4 py-2 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all"
                                    autoComplete="new-password"
                                />
                            </div>

                            {/* Anthropic */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium flex items-center justify-between">
                                    <span>Anthropic</span>
                                    <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">
                                        Get Key →
                                    </a>
                                </label>
                                <input
                                    type="password"
                                    placeholder="sk-ant-..."
                                    value={keys.anthropic || ''}
                                    onChange={(e) => handleKeyChange('anthropic', e.target.value)}
                                    className="w-full bg-background border border-border rounded-lg px-4 py-2 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all"
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 p-3 rounded-lg">
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-border bg-background-tertiary/30 flex justify-end">
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="btn-primary px-6 py-2 flex items-center gap-2"
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="w-4 h-4" />
                                    Save & Get Started
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
